import { useSensor, useSensors, PointerSensor, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type { Post, CalendarData, ViewMode } from '../types';

interface UsePostDragDropProps {
  posts: Post[];
  widgetState: { posts: Post[] | null; viewMode?: ViewMode };
  setWidgetState: (state: { posts: Post[]; viewMode?: ViewMode }) => void;
  viewMode: ViewMode;
}

export function usePostDragDrop({
  posts,
  widgetState,
  setWidgetState,
  viewMode,
}: UsePostDragDropProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    // Table view: row reordering
    if (viewMode === 'table') {
      if (active.id !== over.id) {
        const oldIndex = posts.findIndex(p => p.id === active.id);
        const newIndex = posts.findIndex(p => p.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          // Reorder array
          const reorderedPosts = arrayMove(posts, oldIndex, newIndex);

          // Recalculate order field for all posts based on new positions
          const updatedPosts = reorderedPosts.map((post, index) => ({
            ...post,
            order: index
          }));

          setWidgetState({ ...widgetState, posts: updatedPosts });
        }
      }
      return;
    }

    // Calendar view: drag post to different date
    const draggedPost = active.data.current?.post as Post | undefined;
    const targetDate = over.data.current?.dateStr as string | undefined;

    if (!draggedPost || !targetDate || draggedPost.date === targetDate) return;

    const updatedPosts = posts.map(post => {
      if (post === draggedPost) {
        return { ...post, date: targetDate };
      }
      return post;
    });

    setWidgetState({ ...widgetState, posts: updatedPosts });
  };

  return { sensors, handleDragEnd };
}
